/**
 * Money is integer centavos. Always. No float, no decimal string, no Number
 * with a fractional part ever represents an amount in this system.
 *
 * The only place a decimal appears is at the display boundary, and it is
 * produced here so it is produced identically everywhere.
 */
export const CURRENCY = 'MXN';
export const LOCALE_DEFAULT = 'es-MX';
/** Runtime guard. Anything that is not a safe non-negative integer is a bug. */
export function assertCents(value, label = 'amount') {
    if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
        throw new TypeError(`${label} must be an integer number of centavos, got ${String(value)}`);
    }
}
export function isCents(value) {
    return typeof value === 'number' && Number.isSafeInteger(value);
}
/**
 * Parse operator input ("125", "125.50", "1,250.5", "125,50") into centavos.
 * Returns null when the input is not a valid amount, so callers decide how to
 * surface the problem rather than receiving a silent zero.
 *
 * Acceptance finding F8 (2026-07-31): the original implementation stripped
 * every comma as a thousands separator, so the decimal-comma form "125,50" —
 * ordinary in Mexican handwriting and in CSVs exported from Spanish-locale
 * spreadsheets — silently parsed as 12550 pesos instead of 125.50. A hundred-
 * fold error on a money path. Commas are now interpreted:
 *   - both separators present → the rightmost one is the decimal separator;
 *   - only commas → groups of exactly three digits are thousands
 *     ("1,250" → 1250); a single comma followed by 1–2 digits is a decimal
 *     ("125,50" → 125.50); anything else is rejected;
 *   - only dots → dot is the decimal separator, max two decimals, and a
 *     dot followed by exactly three digits is rejected as ambiguous rather
 *     than guessed at ("1.250" is null, not 1.25 and not 1250).
 */
export function parseAmountToCents(input) {
    let s = input.trim().replace(/[\s$]/g, '');
    if (s === '')
        return null;
    if (!/^[\d.,]+$/.test(s))
        return null;
    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    if (lastDot !== -1 && lastComma !== -1) {
        // Rightmost separator wins as decimal; the other must be well-formed
        // thousands grouping for its side of the number.
        const decimalSep = lastDot > lastComma ? '.' : ',';
        const groupSep = decimalSep === '.' ? ',' : '.';
        const groupRe = new RegExp(`^\\d{1,3}(\\${groupSep}\\d{3})*$`);
        const parts = s.split(decimalSep);
        if (parts.length !== 2)
            return null;
        const [wholeRaw = '', fracRaw = ''] = parts;
        if (!groupRe.test(wholeRaw))
            return null;
        if (!/^\d{1,2}$/.test(fracRaw))
            return null;
        s = `${wholeRaw.split(groupSep).join('')}.${fracRaw}`;
    }
    else if (lastComma !== -1) {
        if (/^\d{1,3}(,\d{3})+$/.test(s)) {
            // Unambiguous thousands grouping: 1,250 / 12,345,678
            s = s.split(',').join('');
        }
        else if (/^\d+,\d{1,2}$/.test(s)) {
            // Single decimal comma with money precision: 125,50
            s = s.replace(',', '.');
        }
        else {
            return null;
        }
    }
    // From here s contains digits and at most one dot.
    if (!/^\d*(\.\d{1,2})?$/.test(s) || /^\.?$/.test(s)) {
        // Allow the bare ".5" form the keypad can produce.
        if (!/^\.\d{1,2}$/.test(s))
            return null;
    }
    const [whole = '0', frac = ''] = s.split('.');
    const padded = (frac + '00').slice(0, 2);
    const cents = Number(whole || '0') * 100 + Number(padded);
    return Number.isSafeInteger(cents) ? cents : null;
}
export function formatCents(cents, locale = LOCALE_DEFAULT) {
    assertCents(cents);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: CURRENCY,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(cents / 100);
}
/** Digits only, for the thermal printer where the glyph set is limited. */
export function formatCentsPlain(cents) {
    assertCents(cents);
    const sign = cents < 0 ? '-' : '';
    const abs = Math.abs(cents);
    return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}
/** Quantities carry at most three decimals, matching numeric(10,3) in the DB. */
const QTY_SCALE = 1000;
/**
 * Multiply an amount by a quantity that may have up to three decimals.
 * Rounds half away from zero, which is what a cashier expects and what the
 * printed ticket must agree with.
 *
 * The multiplication is done in scaled integers rather than as `unit * qty`.
 * Floats make that unreliable at exactly the boundary we care about: 1.005 is
 * not representable, so `100 * 1.005` is 100.49999999999999 and rounds down to
 * 100 when the correct answer is 101. Scaling first removes the question.
 */
export function multiplyCents(unit, qty) {
    assertCents(unit, 'unit price');
    if (!Number.isFinite(qty) || qty <= 0) {
        throw new RangeError(`quantity must be a positive finite number, got ${String(qty)}`);
    }
    const qtyScaled = Math.round(qty * QTY_SCALE);
    const product = unit * qtyScaled;
    if (!Number.isSafeInteger(product)) {
        throw new RangeError('price times quantity exceeded the safe integer range');
    }
    const whole = Math.trunc(product / QTY_SCALE);
    const remainder = product % QTY_SCALE;
    // Half away from zero. Both operands are non-negative here.
    return remainder * 2 >= QTY_SCALE ? whole + 1 : whole;
}
/** Percentage of an amount, rounded half away from zero. */
export function percentOfCents(amount, pct) {
    assertCents(amount);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        throw new RangeError(`percentage must be between 0 and 100, got ${String(pct)}`);
    }
    const exact = (amount * pct) / 100;
    return Math.round(exact);
}
export function sumCents(values) {
    let total = 0;
    for (const v of values) {
        assertCents(v);
        total += v;
    }
    if (!Number.isSafeInteger(total)) {
        throw new RangeError('sum exceeded the safe integer range');
    }
    return total;
}
/** Cash denominations offered as quick buttons, in centavos. */
export const CASH_DENOMINATIONS = [
    2000, 5000, 10000, 20000, 50000, 100000,
];
/**
 * Given a total, suggest the tender amounts a cashier is most likely to
 * receive: the exact total, then the next note up at each denomination.
 */
export function suggestTenders(total, limit = 4) {
    assertCents(total);
    const out = [total];
    for (const d of CASH_DENOMINATIONS) {
        if (out.length >= limit)
            break;
        const rounded = Math.ceil(total / d) * d;
        if (rounded > total && !out.includes(rounded))
            out.push(rounded);
    }
    return out.slice(0, limit);
}
/**
 * Splits `total` across `weights` in proportion, in whole centavos, so the
 * shares sum to exactly `min(total, Σweights)` — largest-remainder method:
 * every share starts at floor(exact) and the leftover units go one each to
 * the largest fractional remainders (ties broken by position, so the result
 * is deterministic). Because a share is never more than ceil(exact), and
 * exact ≤ weight whenever total ≤ Σweights, no share ever exceeds its own
 * weight: allocating an order discount over what each line has left can
 * never push a line below zero. Zero weights receive nothing.
 *
 * Only integer arithmetic touches the result: the floor and the remainder
 * are computed on the integer product `weight × total`, never on a ratio.
 */
export function allocateProportionally(total, weights) {
    assertCents(total, 'total');
    for (const w of weights)
        assertCents(w, 'weight');
    const weightTotal = sumCents(weights);
    if (total <= 0 || weightTotal <= 0)
        return weights.map(() => 0);
    const capped = Math.min(total, weightTotal);
    const products = weights.map((w) => {
        const product = w * capped;
        if (!Number.isSafeInteger(product))
            throw new RangeError('allocation exceeded the safe integer range');
        return product;
    });
    const shares = products.map((p) => Math.floor(p / weightTotal));
    let leftover = capped - sumCents(shares);
    const byRemainder = products
        .map((p, index) => ({ index, remainder: p % weightTotal }))
        .filter((entry) => entry.remainder > 0)
        .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    for (const { index } of byRemainder) {
        if (leftover <= 0)
            break;
        shares[index] = (shares[index] ?? 0) + 1;
        leftover -= 1;
    }
    return shares;
}
/**
 * Unit price of a product sold with chosen options: the MOST EXPENSIVE chosen
 * FLAVOUR (a mix of flavours is charged as its dearest flavour) plus the price
 * of every chosen EXTRA (add-on). Alfredo,
 * 2026-10-18. Pure integer centavos; zero options is the caller's problem
 * (the product's own price applies), so this returns null for it.
 *
 * `mix` is the product's optional mix price (Alfredo, 2026-10-20: three or more
 * savoury flavours cost $160): once at least `minFlavours` FLAVOURS are chosen
 * the flavour part is at least `priceCents`. It never lowers a dearer flavour,
 * and extras never count towards the number.
 */
export function optionsUnitPriceCents(options, mix) {
    const flavours = options.filter((o) => !o.isAddon);
    // Extras alone sell nothing: no flavour, no price (the caller must not add the line).
    if (flavours.length === 0)
        return null;
    let dearest = flavours.reduce((max, o) => (o.priceCents > max ? o.priceCents : max), 0);
    const minFlavours = mix?.mixMinFlavours;
    const mixPrice = mix?.mixPriceCents;
    if (minFlavours != null && mixPrice != null && flavours.length >= minFlavours && mixPrice > dearest) {
        dearest = mixPrice;
    }
    // Extras ADD to the flavour's price (the cheeses are priced 0, so they add nothing today).
    const extras = options.filter((o) => o.isAddon).reduce((sum, o) => sum + o.priceCents, 0);
    return dearest + extras;
}
/**
 * Splits a quantity across `parts` options in thousandths (stock counts are
 * numeric(10,3)); the first part takes the remainder so the parts always add
 * back to `qty` exactly. The ONE rule the server (stock ledger) and the
 * terminal (local mirror) both use to spread a mix of flavours.
 */
export function splitQty(qty, parts) {
    const total = Math.round(qty * 1000);
    const base = Math.floor(total / parts);
    const out = Array.from({ length: parts }, () => base);
    out[0] = (out[0] ?? 0) + (total - base * parts);
    return out.map((n) => n / 1000);
}
//# sourceMappingURL=money.js.map